import React, { useState, useRef, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Button, Grid, Snackbar, Alert, Chip, CircularProgress, LinearProgress, Select, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import {VideocamOff } from '@mui/icons-material';
import PageHeader from './PageHeader';
import { recognizeTest } from '../utils/api';

const primary = '#40518A';

const Test = () => {
  const [items, setItems] = useState([]); // {file, url, result}
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [devices, setDevices] = useState([]); // list of video inputs
  const [inDeviceId, setInDeviceId] = useState('');
  const [outDeviceId, setOutDeviceId] = useState('');
  const [useSameCamera, setUseSameCamera] = useState(true);
  const [activeDirection, setActiveDirection] = useState('IN'); // 'IN' | 'OUT'
  const fileInputRef = useRef(null);

    // Cleanup: stop camera when component unmounts
    useEffect(() => {
      return () => {
        // This runs when component unmounts (navigating away)
        const s = streamRef.current;
        if (s) {
          s.getTracks().forEach(t => t.stop());
        }
        const v = videoRef.current;
        if (v) {
          v.pause();
          v.srcObject = null;
        }
      };
    }, []);
  
  const getConfidenceLabel = (level) => (level ? level.replace('_', ' ') : 'unknown');

  const scorePercent = (s) => {
    const v = Number(s || 0);
    const pct = Math.max(0, Math.min(1, v)) * 100;
    return Math.round(pct);
  };

  const qualityPercent = (v) => {
    const num = Number(v || 0);
    const pct = Math.max(0, Math.min(1, num)) * 100;
    return `${pct.toFixed(1)}%`;
  };

  const profileLabel = (key) => {
    if (!key) return '-';
    if (key === 'left_profile') return 'Left';
    if (key === 'right_profile') return 'Right';
    if (key === 'front') return 'Front';
    return key;
  };

  // Liveness removed

  

  const show = (m, s='success') => setSnackbar({ open: true, message: m, severity: s });

  // Removed file upload option for Test flow

  const enumerateCameras = async () => {
    try {
      const media = await navigator.mediaDevices.enumerateDevices();
      const vids = media.filter(d => d.kind === 'videoinput');
      setDevices(vids);
      if (vids.length > 0) {
        setInDeviceId(prev => prev || vids[0].deviceId);
        setOutDeviceId(prev => prev || (vids[1]?.deviceId || vids[0].deviceId));
        setUseSameCamera(vids.length === 1);
      }
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    enumerateCameras();
  }, []);

  const startCamera = async () => {
    try {
      setCameraOn(true); // ensure video element is rendered
      let constraints = { video: true };
      const deviceId = useSameCamera ? inDeviceId : (activeDirection === 'IN' ? inDeviceId : outDeviceId);
      if (deviceId) constraints = { video: { deviceId: { exact: deviceId } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // Wait a tick to ensure ref is bound after state update
      await new Promise(r => setTimeout(r, 0));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch (_) { /* ignore autoplay issues */ }
      }
    } catch (e) {
      show('Unable to access camera', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.srcObject = null; }
    setCameraOn(false);
  };

  const toggleCamera = async () => {
    if (streamRef.current) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  const switchDirection = async (dir) => {
    setActiveDirection(dir);
    // If already on, restart stream with new device
    if (streamRef.current) {
      stopCamera();
      await startCamera();
    }
  };

  const buildItemsFromFaces = (faces, baseCanvas, fallbackDataUrl) => {
    if (!baseCanvas) return [];
    const width = baseCanvas.width || 640;
    const height = baseCanvas.height || 480;
    return faces.map((f, i) => {
      const bbox = Array.isArray(f.bbox) ? f.bbox : [0, 0, width, height];
      const [x1, y1, x2, y2] = bbox;
      const cw = Math.max(1, x2 - x1);
      const ch = Math.max(1, y2 - y1);
      const crop = document.createElement('canvas');
      crop.width = 400;
      crop.height = 500;
      const ctx = crop.getContext('2d');
      ctx.drawImage(baseCanvas, x1, y1, cw, ch, 0, 0, crop.width, crop.height);
      const cropUrl = crop.toDataURL('image/jpeg', 0.92);
      return { file: null, url: cropUrl, result: f.result, faceIndex: f.face_index || (i + 1) };
    }) || [{ file: null, url: fallbackDataUrl, result: { name: null, confidence: 'no_face', score: 0 } }];
  };

  const createCanvasFromDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 640;
        canvas.height = img.height || 480;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas);
      };
      img.onerror = reject;
      img.src = dataUrl;
    });

  const recognizeDataUrl = async (dataUrl, baseCanvas = null) => {
    try {
      setBusy(true);
      const canvas = baseCanvas || await createCanvasFromDataUrl(dataUrl);
      const res = await recognizeTest(dataUrl, activeDirection);
      const faces = Array.isArray(res.faces) ? res.faces : [];
      const nextItems = faces.length ? buildItemsFromFaces(faces, canvas, dataUrl) : [{ file: null, url: dataUrl, result: { name: null, confidence: 'no_face', score: 0 } }];
      setItems(nextItems);
    } catch (e) {
      show(e.message || 'Recognition failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => {
    items.forEach(i => { if (i.url) URL.revokeObjectURL(i.url); });
    setItems([]); stopCamera();
  };

  const matchCamera = async () => {
    // Capture a frame and send as base64 JSON to backend
    if (!videoRef.current || !streamRef.current) { show('Start the camera first', 'warning'); return; }
    const v = videoRef.current;
    // Ensure video metadata is ready (dimensions > 0)
    let tries = 0;
    while (tries < 10 && (v.readyState < 2 || !v.videoWidth || !v.videoHeight)) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 100));
      tries += 1;
    }
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth || 640; canvas.height = v.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    await recognizeDataUrl(dataUrl, canvas);
  };

  const handleFileInput = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      show('Please select an image file', 'warning');
      event.target.value = '';
      return;
    }
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await recognizeDataUrl(dataUrl);
    } catch (error) {
      show('Failed to read image file', 'error');
    } finally {
      if (event.target) event.target.value = '';
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 3 }}>
      <PageHeader title="Test" subtitle="Open camera, capture a frame, and match faces against preloaded embeddings" icon={null} />

      <Grid container spacing={5} alignItems="stretch">
        {/* Single card: Camera + Camera Selection inside */}
        <Grid item xs={12} sx={{height: '100%', width:"100%"}}>
          <Card sx={{ height: '100%', borderTop: `3px solid ${primary}` }}>
            <CardContent>
              {/* Header tags above preview */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: primary }}>Camera</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip label={cameraOn ? 'Camera: On' : 'Camera: Off'} color="primary" size="small" variant={cameraOn ? 'filled' : 'outlined'} />
                  <Chip label={`Results: ${items.length}`} color="primary" size="small" variant={items.length ? 'filled' : 'outlined'} />
                </Box>
              </Box>
              <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                {/* Left column: preview + buttons */}
                <Box sx={{ flexGrow: 1, minWidth: 520 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      // Responsive height for camera preview
                      height: 'clamp(500px, 50vh, 600px)',
                      mx: 0,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#fafafa',
                    }}
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        background: 'transparent',
                        display: cameraOn ? 'block' : 'none',
                      }}
                    />
                    {!cameraOn && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          color: 'text.secondary',
                          gap: 1,
                        }}
                      >
                        <VideocamOff sx={{ fontSize: 42, color: '#bdbdbd' }} />
                        <Typography variant="body2">Camera is off</Typography>
                      </Box>
                    )}
                    {cameraOn && (
                      <Chip
                        label="Live Preview"
                        size="small"
                        color="primary"
                        sx={{ position: 'absolute', top: 8, left: 8 }}
                      />
                    )}
                  </Box>

                  {/* Buttons below the frame */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
                    <Button
                      variant={cameraOn ? 'outlined' : 'contained'}
                      onClick={toggleCamera}
                      sx={{
                        borderColor: primary,
                        backgroundColor: cameraOn ? 'transparent' : primary,
                        color: cameraOn ? primary : 'white',
                      }}
                    >
                      {cameraOn ? 'Stop Camera' : 'Start Camera'}
                    </Button>
                    <Button variant="contained" onClick={matchCamera} disabled={busy || !cameraOn} sx={{ backgroundColor: primary, minWidth: 170 }}>
                      {busy ? (<><CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> Matching...</>) : 'Start Matching'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={busy}
                      sx={{ borderColor: primary, color: primary }}
                    >
                      Upload Image
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      ref={fileInputRef}
                      onChange={handleFileInput}
                    />
                    <Button variant="text" onClick={clearAll} sx={{ color: primary }}>Clear</Button>
                  </Box>
                </Box>

                {/* Right column: camera selection panel (small fixed width) */}
                <Box sx={{ flex: '0 0 320px', width: 320, maxWidth: 360, position: { md: 'sticky' }, top: { md: 0 } }}>
                  <Typography variant="subtitle2" sx={{ color: primary, mb: 1.25 }}>Select Camera</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <FormControlLabel control={<Switch size="small" checked={useSameCamera} onChange={(e) => setUseSameCamera(e.target.checked)} />} label={<Typography variant="body2">Use same camera for IN & OUT</Typography>} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>IN Camera</Typography>
                      <Select size="small" fullWidth value={inDeviceId} onChange={(e) => setInDeviceId(e.target.value)}>
                        {devices.map(d => (
                          <MenuItem key={d.deviceId} value={d.deviceId} sx={{ py: 0.5 }}>
                            <Typography variant="body2">{d.label || `Camera ${d.deviceId.slice(0,6)}`}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box sx={{ opacity: useSameCamera ? 0.6 : 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>OUT Camera</Typography>
                      <Select size="small" fullWidth value={useSameCamera ? inDeviceId : outDeviceId} onChange={(e) => setOutDeviceId(e.target.value)} disabled={useSameCamera}>
                        {devices.map(d => (
                          <MenuItem key={d.deviceId} value={d.deviceId} sx={{ py: 0.5 }}>
                            <Typography variant="body2">{d.label || `Camera ${d.deviceId.slice(0,6)}`}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Mode</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant={activeDirection === 'IN' ? 'contained' : 'outlined'} onClick={() => switchDirection('IN')} sx={{ textTransform: 'none' }}>IN</Button>
                      <Button size="small" variant={activeDirection === 'OUT' ? 'contained' : 'outlined'} onClick={() => switchDirection('OUT')} sx={{ textTransform: 'none' }}>OUT</Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results header above grid */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1, borderBottom: '1px solid #e0e0e0', pb: 0.5 }}>
        <Typography variant="h6" sx={{ color: primary, fontWeight: 700, letterSpacing: 0.2 }}>Results</Typography>
        <Chip label={`${items.length} face${items.length === 1 ? '' : 's'}`} color="primary" size="small" variant={items.length ? 'filled' : 'outlined'} />
      </Box>
      <Grid container spacing={2}>
        {items.map((it, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card sx={{ borderTop: `3px solid ${primary}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <img src={it.url} alt={`face-${idx}`} style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }} />
                </Box>
                {it.result ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{`Face ${it.faceIndex || (idx+1)}`}</Typography>
                      <Chip size="small" color="primary" label={getConfidenceLabel(it.result.confidence)} />
                    </Box>
                    {
                      <>
                        <Typography variant="h6" sx={{ color: primary, mb: 0.5 }}>{it.result.name || 'No Match'}</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>Person ID: {it.result.id || '-'}</Typography>
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Score: {Number(it.result.score || 0).toFixed(3)} ({scorePercent(it.result.score)}%)</Typography>
                          <LinearProgress color="primary" variant="determinate" value={scorePercent(it.result.score)} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Similarity Threshold: {Number(it.result.similarity_threshold_used || 0).toFixed(3)}
                        </Typography>
                        <Box sx={{ mt: 1.5, p: 1.25, border: '1px solid #e0e0e0', borderRadius: 1.5, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" sx={{ color: primary, mb: 0.75 }}>
                            Image Parameters
                          </Typography>
                          <Grid container spacing={0.75}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Matched Angle</Typography>
                              <Typography variant="body2">{profileLabel(it.result.matched_angle)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Pose Quality</Typography>
                              <Typography variant="body2">{qualityPercent(it.result.pose_quality)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Lighting Quality</Typography>
                              <Typography variant="body2">{qualityPercent(it.result.lighting_quality)}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        {Array.isArray(it.result?.profile_match_debug?.top_people) && it.result.profile_match_debug.top_people.length > 0 && (
                          <Box sx={{ mt: 1.25, p: 1.25, border: '1px solid #e0e0e0', borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                            <Typography variant="subtitle2" sx={{ color: primary, mb: 0.75 }}>
                              Top Profile Matches
                            </Typography>
                            {it.result.profile_match_debug.top_people.slice(0, 2).map((person, pidx) => {
                              const profiles = person?.profiles || {};
                              const front = profiles.front;
                              const left = profiles.left_profile;
                              const right = profiles.right_profile;
                              const custom = profiles.custom;
                              const profileScoreParts = [
                                `Front: ${front != null ? Number(front).toFixed(3) : '-'}`,
                                `Left: ${left != null ? Number(left).toFixed(3) : '-'}`,
                                `Right: ${right != null ? Number(right).toFixed(3) : '-'}`,
                              ];
                              if (custom != null) {
                                profileScoreParts.push(`Custom: ${Number(custom).toFixed(3)}`);
                              }
                              return (
                                <Box key={`${person.person_id || person.name || 'p'}-${pidx}`} sx={{ mb: pidx === 1 ? 0 : 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Top {pidx + 1}: {person?.name || 'Unknown'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Best: {profileLabel(person?.best_profile)} | Score: {Number(person?.best_score || 0).toFixed(3)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {profileScoreParts.join(' | ')}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </>
                    }
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Awaiting match...</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default Test;
